import { useMemo, useState } from 'react';
import { faList } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { SelectPatientCreate } from './_components/select-patient/select-patient-create.component';
import SelectPatientList from './_components/select-patient/select-patient-list.component';
import { AppointmentOptionsDefault } from './_components/appointment-option-default.component';
import { AppointmentOptionsShare } from './_components/appointment-option-share.component';
import { AppointmentConfirmCancel } from './_components/appointment-confirm-cancel.component';
import { AppointmentRescheduleSlotsModal } from './_components/reschedule-slots-modal/appointment-reschedule-slots-modal.component';

import { AppointmentMode, AppointmentsLifecycleEnum } from '@/types/enums/appointments.enum';
import { Appointment } from '@/types/appointments.types';
import { ProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.component';
import { Button } from '@/components/ui/button/button.component';

export enum AppointmentOptionsModalKeys {
  DEFAULT = 'DEFAULT',
  SHARE = 'SHARE',
  REQUEST_CANCELLATION = 'REQUEST_CANCELLATION',
  CHANGE_PATIENT_LIST = 'CHANGE_PATIENT_LIST',
  CHANGE_PATIENT_CREATE = 'CHANGE_PATIENT_CREATE',
  CHANGE_DATE = 'CHANGE_DATE',
}

interface AppointmentsOptionsButtonProps {
  appointment: Appointment;
  onAppointmentUpdate?: (appointment: Partial<Appointment>) => void;
}

export function AppointmentOptionsButton({ appointment, onAppointmentUpdate }: AppointmentsOptionsButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const lifecycle = useMemo(() => {
    if (appointment.hasFinished) {
      return AppointmentsLifecycleEnum.PAST;
    }
    if (appointment.mode === AppointmentMode.STANDALONE) {
      return AppointmentsLifecycleEnum.OTHER;
    }
    return AppointmentsLifecycleEnum.FUTURE;
  }, [appointment]);

  return (
    <>
      <Button
        data-testid={DataTestIdEnum.APPOINTMENTS_OPTIONS_BUTTON}
        startIcon={faList}
        variant="text"
        onClick={() => setIsOpen(true)}
      >
        Opções
      </Button>
      <ProgressiveModal
        sheetSide="centerFit"
        hasHeader={true}
        sheetSideByKey={{
          [AppointmentOptionsModalKeys.CHANGE_DATE]: 'centerWide',
          [AppointmentOptionsModalKeys.CHANGE_PATIENT_LIST]: 'center-v2',
          [AppointmentOptionsModalKeys.CHANGE_PATIENT_CREATE]: 'center-v2',
        }}
        initialKey={AppointmentOptionsModalKeys.DEFAULT}
        open={isOpen}
        handleClose={() => setIsOpen(false)}
      >
        <AppointmentOptionsDefault
          key={AppointmentOptionsModalKeys.DEFAULT}
          appointment={appointment}
          lifecycle={lifecycle}
        />
        <AppointmentOptionsShare key={AppointmentOptionsModalKeys.SHARE} appointment={appointment} />
        <SelectPatientList
          key={AppointmentOptionsModalKeys.CHANGE_PATIENT_LIST}
          appointment={appointment}
        />
        <SelectPatientCreate key={AppointmentOptionsModalKeys.CHANGE_PATIENT_CREATE} />
        <AppointmentConfirmCancel
          lifecycle={lifecycle}
          appointment={appointment}
          key={AppointmentOptionsModalKeys.REQUEST_CANCELLATION}
        />
        <AppointmentRescheduleSlotsModal
          key={AppointmentOptionsModalKeys.CHANGE_DATE}
          appointment={appointment}
          onAppointmentUpdate={onAppointmentUpdate}
        />
      </ProgressiveModal>
    </>
  );
}
